<script setup>
/**
 * FormDuplicateBtn
 * Handles record duplication by sending a copy request to the API.
 * Includes complex redirection logic for standard forms, the Site Tree,
 * and the Page Editor tool.
 */
import {ref, computed} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import FormSidebarBtn from "./FormSidebarBtn.vue"
import api from '@/services/api'
import {usePageContentDataStore} from "@/stores/pageContentData"
import {usePageEditorStore} from "@/stores/pageEditor"
import {useCategoriesStore} from "@/stores/categories"
import {useFormStore} from "@/stores/form"
import {useSiteTreeStore} from "@/stores/siteTree"

const props = defineProps({
    formId: {
        type: String,
        required: true
    }
})

// Composables
const route = useRoute()
const router = useRouter()

// Pinia Stores
const pageContentDataStore = usePageContentDataStore()
const pageEditorStore = usePageEditorStore()
const categoriesStore = useCategoriesStore()
const formStore = computed(() => props.formId ? useFormStore(props.formId) : null)
const siteTreeStore = useSiteTreeStore('siteTree')

// --- State ---

const isDisabled = ref(false)

// --- Computed ---

const tableName = computed(() => formStore.value?.tableName)

const id = computed(() => formStore.value?.id)

const show = computed(() => {
    return (
        id.value && // Don't show when creating a new item
        tableName.value !== 'account_settings' &&
        tableName.value !== 'product_options' &&
        tableName.value !== 'product_variables' &&
        tableName.value !== 'm_basket_orders' &&
        tableName.value !== 'm_registrants_registrants' &&
        tableName.value !== 'users'
    )
})

const isSiteTree = computed(() => route.name === 'siteTree')

const isPageEditor = computed(() => route.name === 'pageEditor')

const pageContentId = computed(() => {
    if (isPageEditor.value) {
        return route.query.id
    } else {
        const part = route.path.split('/').pop() || ''
        return (part.indexOf('c') === 0) ? parseInt(part.substring(1)) : 0
    }
})

// --- Methods ---

const onClick = async () => {
    if (!confirm('Are you sure?')) {
        return
    }

    isDisabled.value = true

    try {
        const {data: {id: newId}} = await api.post(`api/component/${tableName.value}?copyId=${id.value}&pageContentId=${pageContentId.value}`)

        if (tableName.value === 'pages' || tableName.value === 'content') {
            await siteTreeStore.init()
        }

        // Ensure category caches are refreshed so the new item appears in UI components
        await categoriesStore.refreshComponentData({
            tableName: tableName.value
        })

        if (isSiteTree.value) {
            await pageContentDataStore.init()
            isDisabled.value = false

            if (tableName.value === 'content') {
                const obj = pageContentDataStore.items.find(o => o.contentId === newId)
                if (obj) {
                    await router.push({
                        name: 'siteTree',
                        params: {pageOrContentId: 'c' + obj.id},
                        query: route.query
                    })
                }
            } else {
                await router.push({
                    name: 'siteTree',
                    params: {pageOrContentId: newId},
                    query: route.query
                })
            }
        } else if (isPageEditor.value) {
            isDisabled.value = false

            await pageContentDataStore.init()
            const o = pageContentDataStore.items.find(o => o.contentId === newId)
            const newPageContentId = o.id
            const contentAreaName = o.contentArea

            await pageEditorStore.placeContentInPage({
                pageContentId: newPageContentId,
                contentAreaName
            })

            await pageEditorStore.goToContent({
                router: router,
                pageContentId: newPageContentId
            })
        } else {
            isDisabled.value = false

            await router.push({
                name: 'form',
                params: {id: newId},
                query: route.query
            })
        }
    } catch (error) {
        isDisabled.value = false
        console.error("Duplication failed:", error)
    }
}
</script>

<template>
    <FormSidebarBtn v-if="show" :disabled="isDisabled" @click.prevent="onClick">
        <i class="me-2 bi-subtract"/>
        Duplicate
    </FormSidebarBtn>
</template>